import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { isDefined, isNil } from '@trimble-gcs/common';
import { catchError, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { PageData } from '../../shared/page-data.model';
import { GET_SCAN_PROJECT_URL } from '../../utils/get-scan-project-url';
import { getScandataQuerystring } from '../scandata-query';
import { ResourceStatusFilter } from '../scandata-query.models';
import { AddScans, RemoveScandataModels } from '../scandata.actions';
import { ScandataBatchOperationResult, ScandataModel } from '../scandata.models';
import { ScandataService } from '../scandata.service';
import { RemoveRecycledScans, SetRecycledScans } from './scandata-recycle.actions';
import { ScandataRecycleState } from './scandata-recycle.state';

@Injectable({
  providedIn: 'root',
})
export class ScandataRecycleService {
  private readonly getScanProjectUrl$ = inject(GET_SCAN_PROJECT_URL);
  private readonly store = inject(Store);
  private readonly http = inject(HttpClient);
  private readonly scandataService = inject(ScandataService);

  loadRecycledScans(): Observable<void> {
    const querystring = getScandataQuerystring({
      filters: { resourceStatus: [ResourceStatusFilter.Deleted] },
      sortInfo: this.store.selectSnapshot(ScandataRecycleState.sortInfo),
      pageInfo: { pageIndex: 0, pageSize: 0 },
    });

    return this.getScanProjectUrl$(`/pointclouds?${querystring}`).pipe(
      switchMap((url) => this.http.get<PageData<ScandataModel>>(url)),
      map((pageData) => pageData.items.map((scan) => this.scandataService.mapScandataModel(scan))),
      switchMap((data) => this.store.dispatch(new SetRecycledScans(data))),
    );
  }

  restoreScans(scans: ScandataModel[]): Observable<ScandataBatchOperationResult> {
    const updateList$: Observable<{ scan: ScandataModel; error?: HttpErrorResponse }>[] = scans.map(
      (scan) => {
        return this.getScanProjectUrl$(`/pointclouds/${scan.id}/restore`).pipe(
          switchMap((url) => this.http.post<ScandataModel>(url, null)),
          map(() => ({ scan })),
          catchError((error: HttpErrorResponse) => of({ scan, error })),
        );
      },
    );

    return forkJoin(updateList$).pipe(
      map((results) => {
        const successful = results
          .filter((result) => isNil(result.error))
          .map((result) => result.scan);

        const failed = results
          .filter((result) => isDefined(result.error))
          .map((result) => result.scan);

        return { successful, failed };
      }),
      switchMap(({ successful, failed }) => {
        return this.store
          .dispatch([
            new RemoveRecycledScans(successful.map((item) => item.id)),
            new AddScans(
              successful.map((item) => ({
                ...this.scandataService.assignWeb3dId(item),
                selected: false,
              })),
            ),
          ])
          .pipe(map(() => ({ successful, failed })));
      }),
    );
  }

  deleteScan(scan: ScandataModel): Observable<void> {
    return this.getScanProjectUrl$(`/pointclouds/${scan.id}`).pipe(
      switchMap((url) => this.http.delete<void>(url)),
      switchMap(() => this.store.dispatch(new RemoveScandataModels([scan.id]))),
    );
  }

  deleteScans(scans: ScandataModel[]): Observable<ScandataBatchOperationResult> {
    const deleteList$: Observable<{ scan: ScandataModel; error?: HttpErrorResponse }>[] = scans.map(
      (scan) => {
        return this.getScanProjectUrl$(`/pointclouds/${scan.id}`).pipe(
          switchMap((url) => this.http.delete<void>(url)),
          map(() => ({ scan })),
          catchError((error: HttpErrorResponse) => of({ scan, error })),
        );
      },
    );

    return forkJoin(deleteList$).pipe(
      map((results) => {
        const successful = results
          .filter((result) => isNil(result.error))
          .map((result) => result.scan);

        const failed = results
          .filter((result) => isDefined(result.error))
          .map((result) => result.scan);

        return { successful, failed };
      }),
      switchMap(({ successful, failed }) => {
        const removeScanIds = successful.map((item) => item.id);
        return this.store
          .dispatch(new RemoveScandataModels(removeScanIds))
          .pipe(map(() => ({ successful, failed })));
      }),
    );
  }

  deleteScansPermanently(scans: ScandataModel[]): Observable<ScandataBatchOperationResult> {
    const deleteList$: Observable<{ scan: ScandataModel; error?: HttpErrorResponse }>[] = scans.map(
      (scan) => {
        return this.getScanProjectUrl$(`/pointclouds/${scan.id}?permanent=true`).pipe(
          switchMap((url) => this.http.delete<void>(url)),
          map(() => ({ scan })),
          catchError((error: HttpErrorResponse) => of({ scan, error })),
        );
      },
    );

    return forkJoin(deleteList$).pipe(
      map((results) => {
        const successful = results
          .filter((result) => isNil(result.error))
          .map((result) => result.scan);

        const failed = results
          .filter((result) => isDefined(result.error))
          .map((result) => result.scan);

        return { successful, failed };
      }),
      switchMap(({ successful, failed }) => {
        const removeScanIds = successful.map((item) => item.id);
        return this.store
          .dispatch([
            new RemoveScandataModels(removeScanIds),
            new RemoveRecycledScans(removeScanIds),
          ])
          .pipe(map(() => ({ successful, failed })));
      }),
    );
  }
}
